// HomeAnimated.jsx (Brand Color Version)
import { useRef } from "react";
import { MainContainer, ServiceStack, TechStack } from "../components";
import { PAGES } from "../constants";
import { useNavigate } from "react-router-dom";
import { HiOutlineArrowRight } from "react-icons/hi";
import { Link as ScrollLink } from "react-scroll";
import { motion } from "framer-motion";

export default function HomeAnimated() {
  const navigate = useNavigate();
  const heroRef = useRef(null);
  const benefits = [
    {
      id: 1,
      serialNo: "01",
      title: "Tailored Solutions",
      content:
        "Every business is unique — so our solutions are custom-built for your goals. From apps to websites, we create exactly what you need.",
    },
    {
      id: 2,
      serialNo: "02",
      title: "Smart Investment",
      content:
        "Transparent pricing, no surprises. You get high-quality tech solutions that fit your budget and maximize value.",
    },
    {
      id: 3,
      serialNo: "03",
      title: "Flexible & Scalable",
      content:
        "Your business grows — and your tech should too. Our solutions scale easily and adapt as your needs evolve.",
    },
    {
      id: 4,
      serialNo: "04",
      title: "Expert Team",
      content:
        "With deep domain experience and modern tech skills, our team delivers reliable, future-ready solutions.",
    },
    {
      id: 5,
      serialNo: "05",
      title: "Quality You Can Trust",
      content:
        "We follow industry best practices to build fast, secure, and dependable products you can rely on.",
    },
    {
      id: 6,
      serialNo: "06",
      title: "Long-Term Partnership",
      content:
        "We’re here for the long run — with dedicated support, quick responses, and a focus on your success.",
    },
  ];

  return (
    <MainContainer>
      {/* ========= HERO ========= */}
      <section className="relative overflow-hidden" ref={heroRef}>
        {/* Background Gradient Shapes */}
        <div
          className="absolute -left-40 -top-24 w-[480px] h-[480px] rounded-full 
                  bg-primary-color/15 blur-3xl pointer-events-none"
        />

        <div
          className="absolute right-[-120px] top-10 w-[420px] h-[420px] rounded-full 
                  bg-gray-300/20 blur-3xl pointer-events-none"
        />

        <div className="max-w-7xl mx-auto px-8 lg:px-28 py-24 relative z-10">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            {/* LEFT TEXT */}
            <div className="max-w-xl">
              <motion.h1
                initial={{ opacity: 0, x: -40 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                className="text-4xl md:text-6xl lg:text-5xl font-bold text-gray-900 leading-tight"
              >
                <span className="block">Design.</span>
                <span className="block">Develop.</span>
                <span className="block text-primary-color">
                  Deliver — Better. Faster. Smarter.
                </span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
                className="mt-6 text-gray-700 text-lg leading-relaxed"
              >
                Embark on a business revolution empowered by cutting-edge
                technology, propelling growth and innovation to unprecedented
                heights.
              </motion.p>

              {/* CTA BUTTONS */}
              <div className="mt-8 flex gap-4">
                <ScrollLink
                  to="services"
                  smooth
                  className="bg-primary-color text-white px-5 py-2 rounded-full font-semibold shadow-lg hover:opacity-90 transition cursor-pointer"
                >
                  Browse Services
                </ScrollLink>

                <button
                  onClick={() => navigate(PAGES.CONTACT)}
                  className="border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-100 transition"
                >
                  Get In Touch
                </button>
              </div>
            </div>

            {/* RIGHT HERO AREA */}
            <div className="hidden lg:flex  justify-center items-center relative h-full">
              {/* MAIN ILLUSTRATION (NOT DRAGGABLE) */}
              <div className="relative w-[520px] h-[340px] flex items-end justify-center">
                <img
                  src={require("../assets/images/landing.png")}
                  alt="Landing"
                  className="w-3/4 object-contain drop-shadow-xl"
                  draggable={false}
                />
              </div>
              <div className="absolute top-5">
                {/* LEFT CARD — FIXED START POSITION + DRAGGABLE */}
                <motion.div
                  drag
                  dragConstraints={heroRef}
                  dragElastic={0.18}
                  dragMomentum={false}
                  initial={{ x: -200, y: -50 }}
                  animate={{ y: [-50, -60, -50] }}
                  transition={{
                    duration: 6,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                  className="absolute w-64 h-40 bg-white shadow-xl rounded-2xl p-6 border border-gray-100 cursor-grab active:cursor-grabbing"
                >
                  <h4 className="text-primary-color font-bold">Design First</h4>
                  <p className="mt-2 text-sm text-gray-600">
                    Beautiful UI & strong UX foundations.
                  </p>
                </motion.div>

                {/* RIGHT CARD — FIXED START POSITION + DRAGGABLE */}
                <motion.div
                  drag
                  dragConstraints={heroRef}
                  dragElastic={0.18}
                  dragMomentum={false}
                  initial={{ x: 50, y: -40 }}
                  animate={{ y: [-40, -46, -40] }}
                  transition={{ duration: 5, repeat: Infinity }}
                  className="absolute w-72 h-44 bg-white shadow-xl rounded-2xl p-6 border border-gray-100 cursor-grab active:cursor-grabbing"
                >
                  <h4 className="text-primary-color font-bold">
                    Scalable Code
                  </h4>
                  <p className="mt-2 text-sm text-gray-600">
                    Architecture built for growth.
                  </p>
                </motion.div>

                {/* THIRD DRAGGABLE CARD */}
                <motion.div
                  drag
                  dragConstraints={heroRef}
                  dragElastic={0.18}
                  dragMomentum={false}
                  initial={{ x: -40, y: 70 }}
                  animate={{ y: [70, 62, 70] }}
                  transition={{
                    duration: 7,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                  className="absolute w-64 h-40 bg-white shadow-xl rounded-2xl p-6 
             border border-gray-100 cursor-grab active:cursor-grabbing"
                >
                  <h4 className="text-primary-color font-bold">
                    Rapid Delivery
                  </h4>
                  <p className="mt-2 text-sm text-gray-600">
                    Fast development cycles with reliable execution.
                  </p>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========= COMPANY OVERVIEW ========= */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-8 lg:px-28 grid md:grid-cols-2 gap-8">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="p-6 rounded-2xl bg-gray-50 border border-gray-200 shadow-md"
          >
            <h3 className="text-2xl font-semibold text-gray-900">
              GitSetCode Software Solutions
            </h3>
            <p className="mt-4 text-gray-700">
              GitSetCode Software Solutions is a reliable and principled digital
              technology company committed to ensuring the success of their
              clients.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="p-6 rounded-2xl bg-gray-50 border border-gray-200 shadow-md"
          >
            <h3 className="text-2xl font-semibold text-gray-900">
              Foremost IT Services Provider
            </h3>
            <p className="mt-4 text-gray-700">
              Our comprehensive IT services encompass consultation and
              proficient execution of your projects.
            </p>

            <button
              onClick={() => navigate(PAGES.ABOUT)}
              className="inline-flex items-center gap-2 bg-primary-color text-white px-4 py-2 mt-6 rounded-full font-semibold shadow-md hover:opacity-90 transition"
            >
              More About GSC <HiOutlineArrowRight />
            </button>
          </motion.div>
        </div>
      </section>

      {/* ========= BENEFITS ========= */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-8 lg:px-28">
          <h3 className="text-center text-3xl font-bold text-gray-900 mb-10">
            A Tech Firm Focused on Solutions
          </h3>

          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {benefits.map((b, i) => (
              <motion.div
                key={b.id}
                whileHover={{ scale: 1.03, y: -6 }}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="bg-white border border-gray-200 rounded-2xl p-6 shadow-md hover:shadow-xl transition relative"
              >
                <div className="text-primary-color opacity-10 text-6xl absolute select-none top-[-30px] pointer-events-none">
                  {b.serialNo}
                </div>
                <h4 className="text-xl font-bold text-gray-900">{b.title}</h4>
                <p className="mt-3 text-gray-600 text-sm">{b.content}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <TechStack />

      <div id="services" className="mt-20">
        <ServiceStack />
      </div>
    </MainContainer>
  );
}
